import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TablePagination,
  Button,
} from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import DeleteDialogBox from "../../../components/dialogBox/DeleteDialogBox";
import useToast from "../../../helpers/useToast";
import { toast } from "react-toastify";
import useEncryption from "../../../services/useEncryption";
import useHeaders from "../../../helpers/useHeaders";
import { useOutletContext } from "react-router-dom";

// API imports (update these names as per your files)
import {
  getLocationQuestionsPaginated,
  deleteLocationQuestion,
} from "../../../api/typeFormQuestionApi";

import AdminLocationQuestionModal from "./AdminLocationQuestionModal";

const headCells = [
  { id: "questionVariable", label: "Question Variable" },
  { id: "customVariable", label: "Custom Variable" },
  { id: "actions", label: "Actions" },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) =>
    onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id}>
            {headCell.id !== "actions" ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const LocationQuestionListComponent = () => {
  const { decryptData } = useEncryption();
  const toastOptions = useToast();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const [loading, setLoading] = useOutletContext();

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, search]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await getLocationQuestionsPaginated(
        page + 1,
        rowsPerPage,
        search
      );

      const decrypted = decryptData(res?.data?.data);

      setData(decrypted.questions);
      setTotalCount(decrypted.totalDocuments);

      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error("Failed to load questions", toastOptions);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDeleteClick = (row) => {
    setDeleteItem(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteLocationQuestion(deleteItem._id);
      toast.success(res.data.message, toastOptions);

      setIsDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Delete failed", toastOptions);
    }
  };

  return (
    <div className="tableDesigned">
      {/* ╔════════ SEARCH + ADD BUTTON ROW ═══════╗ */}
      <div className="topHeader">
        <div className="d-flex align-items-center justify-content-between">
          <div className="searchBar">
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setPage(0);
                setSearch(e.target.value);
              }}
            />
          </div>

          <Button
            className="btn blackoutlineBtn"
            onClick={() => setOpenAdd(true)}
          >
            + Add Location Question
          </Button>
        </div>
      </div>
      {/* ╚═════════════════════════════════════════╝ */}

      <Box>
        <Paper>
          <TableContainer>
            <Table>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      style={{ padding: "40px 0" }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, index) => (
                    <TableRow key={row._id} hover>
                      <TableCell>{row.questionVariable}</TableCell>
                      <TableCell>{row.customVariable}</TableCell>

                      <TableCell>
                        <div className="btn-groups">
                          <button
                            onClick={() => handleDeleteClick(row)}
                            className="actionbuttons"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="25"
                              viewBox="0 0 24 25"
                              fill="none"
                            >
                              <path
                                d="M18 7.48047H18.5C19.0523 7.48047 19.5 7.03275 19.5 6.48047V5.98047C19.5 5.42819 19.0523 4.98047 18.5 4.98047H15.5M18 7.48047V19.4805C18 20.5851 17.1046 21.4805 16 21.4805H8C6.89543 21.4805 6 20.5851 6 19.4805V7.48047M18 7.48047H6M15.5 4.98047V4.48047C15.5 3.3759 14.6046 2.48047 13.5 2.48047H10.5C9.39543 2.48047 8.5 3.3759 8.5 4.48047V4.98047M15.5 4.98047H8.5M6 7.48047H5.5C4.94772 7.48047 4.5 7.03275 4.5 6.48047V5.98047C4.5 5.42819 4.94772 4.98047 5.5 4.98047H8.5M10 10.9805V17.9805M14 10.9805V17.9805"
                                stroke="#35353A"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, np) => setPage(np)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </Paper>
      </Box>

      {/* Delete Confirmation */}
      <DeleteDialogBox
        isDeleteOpen={isDeleteOpen}
        onDeleteClose={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Location Question"
        description="Are you sure you want to remove this location question?"
      />

      {/* Add/Edit Modal */}
      <AdminLocationQuestionModal
        open={openAdd}
        handleClose={() => {
          setOpenAdd(false);
          setOpenEdit(false);
        }}
        data={editData}
        refresh={fetchData}
      />
    </div>
  );
};
